import { useCallback, useState } from "react";
import { doc, runTransaction } from "firebase/firestore";
import { useTranslation } from "react-i18next";
import { db } from "@/integrations/firebase/firestore";
import { logError } from "@/shared/services/ErrorReporting";
import { toast } from "@/utils/toast";

export function useAddTenantAdmin(tenantId: string, tenantName: string) {
  const { t } = useTranslation(["admin"]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const addAdmin = useCallback(
    async (uid: string, email: string): Promise<boolean> => {
      setIsSubmitting(true);
      try {
        const tenantRef = doc(db, "tenants", tenantId);

        await runTransaction(db, async (tx) => {
          const snap = await tx.get(tenantRef);
          if (!snap.exists()) {
            throw new Error(`Tenant ${tenantId} does not exist`);
          }

          const data = snap.data();
          const currentAdminUids: string[] = data.admin_uids ?? [];

          if (currentAdminUids.includes(uid)) {
            return;
          }

          tx.update(tenantRef, {
            admin_uids: [...currentAdminUids, uid],
          });
        });

        toast.success(
          t("admin:tenants.detail.addAdminDialog.success", {
            email,
            tenantName,
          }),
        );
        return true;
      } catch (err) {
        logError(err as Error);
        toast.error(
          t("admin:tenants.detail.addAdminDialog.errorWriteFailed", {
            tenantName,
          }),
        );
        return false;
      } finally {
        setIsSubmitting(false);
      }
    },
    [tenantId, tenantName, t],
  );

  return { addAdmin, isSubmitting };
}
